import { baseApi } from './baseApi';

/** Must match backend validation for POST /files/presign */
export const INSTITUTION_UPLOAD_PERMITTED_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'xlsx',
  'xls',
  'csv',
  'zip',
  'ppt',
  'pptx',
  'txt',
  'md',
  'mp4',
  'webm',
  'mp3',
  'wav',
] as const;

export type InstitutionPresignFileType =
  | 'document'
  | 'image'
  | 'video'
  | 'audio'
  | 'url'
  | 'pdf'
  | 'zip'
  | 'word'
  | 'excel'
  | 'powerpoint'
  | 'spreadsheet'
  | 'other';

export interface PresignFileRequest {
  fileName: string;
  folder: string;
  fileType: InstitutionPresignFileType;
}

export interface PresignUploadParams {
  apiKey: string;
  cloudName: string;
  timestamp: number;
  signature: string;
  folder: string;
  uploadUrl: string;
}

export interface PresignFileResult {
  fileId: string;
  uploadParams: PresignUploadParams;
}

export interface InstitutionFileRecord {
  id: string;
  tenantId?: string;
  fileName: string;
  publicId?: string | null;
  url: string;
  secureUrl: string;
  fileType: string;
  status?: string;
  size: number;
  width?: number | null;
  height?: number | null;
  uploadedBy?: string;
  folder?: string;
  metadata?: Record<string, unknown> | null;
  expiresAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export interface CloudinaryUploadResultPayload {
  public_id: string;
  url?: string;
  secure_url?: string;
  format: string;
  bytes: number;
}

export interface ConfirmFileUploadRequest {
  fileId: string;
  uploadResult: CloudinaryUploadResultPayload;
}

function pickRecord(root: Record<string, unknown>): Record<string, unknown> | null {
  if (root.response && typeof root.response === 'object' && !Array.isArray(root.response)) {
    return root.response as Record<string, unknown>;
  }
  if (root.data && typeof root.data === 'object' && !Array.isArray(root.data)) {
    return root.data as Record<string, unknown>;
  }
  return null;
}

function normalizeUploadParams(raw: Record<string, unknown>): PresignUploadParams {
  return {
    apiKey: String(raw.apiKey ?? raw.api_key ?? ''),
    cloudName: String(raw.cloudName ?? raw.cloud_name ?? ''),
    timestamp: Number(raw.timestamp ?? 0),
    signature: String(raw.signature ?? ''),
    folder: String(raw.folder ?? ''),
    uploadUrl: String(raw.uploadUrl ?? raw.upload_url ?? ''),
  };
}

function unwrapPresignFileResponse(response: unknown): PresignFileResult {
  if (!response || typeof response !== 'object') {
    throw new Error('Invalid presign response');
  }
  const root = response as Record<string, unknown>;
  const inner = pickRecord(root) ?? (typeof root.fileId === 'string' ? root : null);
  if (!inner || typeof inner !== 'object') {
    throw new Error('Invalid presign response shape');
  }
  const fileId = String(inner.fileId ?? '');
  const upRaw = inner.uploadParams ?? inner.upload_params;
  if (!fileId || !upRaw || typeof upRaw !== 'object') {
    throw new Error('Invalid presign response: missing fileId or uploadParams');
  }
  return {
    fileId,
    uploadParams: normalizeUploadParams(upRaw as Record<string, unknown>),
  };
}

function mapInstitutionFile(raw: Record<string, unknown>): InstitutionFileRecord {
  return {
    id: String(raw.id ?? ''),
    tenantId: raw.tenantId != null ? String(raw.tenantId) : undefined,
    fileName: String(raw.fileName ?? raw.file_name ?? ''),
    publicId: raw.publicId != null ? String(raw.publicId) : raw.public_id != null ? String(raw.public_id) : null,
    url: String(raw.url ?? ''),
    secureUrl: String(raw.secureUrl ?? raw.secure_url ?? raw.url ?? ''),
    fileType: String(raw.fileType ?? raw.file_type ?? 'other'),
    status: raw.status != null ? String(raw.status) : undefined,
    size: Number(raw.size ?? raw.bytes ?? 0),
    width: raw.width != null ? Number(raw.width) : undefined,
    height: raw.height != null ? Number(raw.height) : undefined,
    uploadedBy: raw.uploadedBy != null ? String(raw.uploadedBy) : undefined,
    folder: raw.folder != null ? String(raw.folder) : undefined,
    metadata: (raw.metadata as Record<string, unknown> | null) ?? null,
    expiresAt: raw.expiresAt != null ? String(raw.expiresAt) : null,
    createdAt: raw.createdAt != null ? String(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
    deletedAt: raw.deletedAt != null ? String(raw.deletedAt) : null,
  };
}

function unwrapConfirmFileResponse(response: unknown): InstitutionFileRecord {
  if (!response || typeof response !== 'object') {
    throw new Error('Invalid confirm response');
  }
  const root = response as Record<string, unknown>;
  const inner = pickRecord(root);
  const fileRaw =
    inner && typeof inner === 'object' && inner.file && typeof inner.file === 'object'
      ? (inner.file as Record<string, unknown>)
      : root.file && typeof root.file === 'object'
        ? (root.file as Record<string, unknown>)
        : inner && typeof inner.id === 'string'
          ? inner
          : null;
  if (!fileRaw) {
    throw new Error('Invalid confirm response: missing file');
  }
  return mapInstitutionFile(fileRaw);
}

/** Infer presign `fileType` from the client file name (extension). */
export function inferInstitutionPresignFileType(fileName: string): InstitutionPresignFileType {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) return 'image';
  if (['mp4', 'webm', 'mov', 'mkv'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(ext)) return 'audio';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'zip') return 'zip';
  if (['doc', 'docx'].includes(ext)) return 'word';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
  if (['ppt', 'pptx'].includes(ext)) return 'powerpoint';
  if (ext === 'txt' || ext === 'md' || ext === 'rtf') return 'document';
  return 'other';
}

export function isPermittedInstitutionUploadFileName(fileName: string): boolean {
  const ext = fileName.includes('.') ? (fileName.split('.').pop()?.toLowerCase() ?? '') : '';
  if (!ext) return false;
  return (INSTITUTION_UPLOAD_PERMITTED_EXTENSIONS as readonly string[]).includes(ext);
}

export const filesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    presignFile: builder.mutation<PresignFileResult, PresignFileRequest>({
      query: (body) => ({
        url: '/files/presign',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => unwrapPresignFileResponse(response),
    }),

    confirmFileUpload: builder.mutation<InstitutionFileRecord, ConfirmFileUploadRequest>({
      query: (body) => ({
        url: '/files/confirm',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => unwrapConfirmFileResponse(response),
    }),
  }),
});

export const { usePresignFileMutation, useConfirmFileUploadMutation } = filesApi;
